import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { deliveryNoteApi } from '../api/deliveryNoteApi'
import { InventoryItem } from './types'
import { DelyveryNotesState } from './types'
import { appStore } from '@/app/store/appStore'
import { RootState } from '@/app/store/appStore'
import { data } from 'autoprefixer'

const initialState: DelyveryNotesState = {
  data: {
    items: [],
    packing_details: '',
    instructions: '',
    vehicle_transporter: '',
    vehicle_number_plate: '',
    driver_name: '',
    driver_phone: '',
    delivery_place: '',
    receiver_name: '',
    receiver_phone: '',
  },
  loading: false,
  error: null,
  step: 1,
}

type SetFieldValuePayload<T> = {
  name: keyof T
  value: T[keyof T]
}

const deliveryNoteSlice = createSlice({
  name: 'deliveryNote',
  initialState,
  reducers: {
    setInitState(state) {
      state.data = initialState.data
      state.error = null
      state.loading = false
      state.step = 1
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },
    setFieldValue(
      state,
      {
        payload,
      }: PayloadAction<SetFieldValuePayload<DelyveryNotesState['data']>>,
    ) {
      state.data[payload.name] = payload.value
    },

    setItemQty(
      state,
      { payload }: PayloadAction<{ value: number; index: number; id: number }>,
    ) {
      state.data.items = state.data.items.map(item =>
        item.id === payload.id ? { ...item, amount: payload.value } : item,
      )
    },
    setInventoryItemType(
      state,
      {
        payload,
      }: PayloadAction<{
        itemId: number
        permanentId: number
        available_amount: number
        name: string
      }>,
    ) {
      const { itemId, permanentId, available_amount, name } = payload

      if (state.data.items[permanentId]) {
        const oldItem = state.data.items[permanentId]
        delete state.data.items[permanentId]

        state.data.items[itemId] = {
          ...oldItem,
          asset_type_id: itemId,
          id: itemId,
          available_amount,
          name,
        }
      }
    },
    addInventoryItem(state, action: PayloadAction<InventoryItem>) {
      state.data.items = {
        ...state.data.items,
        [action.payload.asset_type_id]: action.payload,
      }
    },
    removeInventoryItem(state, action: PayloadAction<number>) {
      delete state.data.items[action.payload]
    },
    setInventoryDeletedItemIds(state, action: PayloadAction<number>) {
      state.data.deletedItems = [...state.data.deletedItems, action.payload]
    },
    removeInventoryDeletedItemIds(state, action: PayloadAction<number>) {
      state.data.deletedItems = state.data.deletedItems.filter(
        item => item !== action.payload,
      )
    },
    setInitialItems(state, action: PayloadAction<InventoryItem[]>) {
      state.data.items = action.payload
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      deliveryNoteApi.endpoints.getDeliveryNote.matchFulfilled,
      (state, action) => {
        state.loading = false
        state.error = null
        state.data = action.payload
      },
    )
    //   .addMatcher(
    //     workorderApi.endpoints.getWorkOrderDetail.matchFulfilled,
    //     (state, action: PayloadAction<WorkOrder>) => {
    //       state.loading = false
    //     },
    //   )
    //   .addMatcher(
    //     workorderApi.endpoints.getWorkOrderDetail.matchRejected,
    //     (state, action) => {
    //       state.loading = false
    //     },
    //   )
  },
})

const selectInventoryState = (state: RootState) => state

const selectItems = (state: RootState) => state.deliveryNote.data.items

const selectFormData = (state: RootState): DelyveryNotesState['data'] =>
  state.deliveryNote.data

export const selectInventoryItems = createSelector(
  [selectInventoryState],
  inventoryState => Object.values(inventoryState.data.items),
)

export const selectInventoryItemsByCategory = (categoryId: number) =>
  createSelector([selectItems], items =>
    Object.values(items)
      .filter(item => item.asset_category_id === categoryId)
      .sort((a, b) => a.index - b.index),
  )

export const formDataSelector = createSelector([selectFormData], data => data)

export const {
  setLoading,
  setError,
  setItemQty,
  setInventoryItemType,
  addInventoryItem,
  removeInventoryItem,
  setFieldValue,
  setInventoryDeletedItemIds,
  removeInventoryDeletedItemIds,
  setInitState,
  setInitialItems,
} = deliveryNoteSlice.actions

export default deliveryNoteSlice
